import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Users, MapPin } from 'lucide-react';
import { Avatar } from '../Profile/Avatar';
import { AvatarGroup } from '../ui/avatar-group';
import type { Community } from '../../types/communities';

// Official group owner ID
const OFFICIAL_OWNER_ID = '8a5791a8-8dbc-4c49-a146-f5768d0007ed';

interface MemberAvatar {
  user_id: string;
  avatar_url?: string;
  username?: string;
}

interface CommunityCardProps {
  community: Community & { memberAvatars?: MemberAvatar[] };
  disabled?: boolean;
  distance?: number;
}

export function CommunityCard({ community, disabled, distance }: CommunityCardProps) {
  const { t } = useTranslation();
  const CardContent = () => (
    <div className={`transition-all duration-150 cursor-pointer active:scale-[0.98] ${
      disabled ? 'opacity-80' : ''
    }`}>
      <div className="aspect-w-16 aspect-h-9 relative rounded-2xl overflow-hidden">
        {community.banner_url && (
          <img
            src={community.banner_url}
            alt={community.name}
            className="w-full h-48 object-cover"
          />
        )}
        {!community.banner_url && (
          <div
            className="w-full h-48 flex items-center justify-center"
            style={{ backgroundColor: '#F4F7EE' }}
          >
            <span className="text-3xl font-semibold text-accent-text font-gelica">
              Community
            </span>
          </div>
        )}
        {community.owner_id === OFFICIAL_OWNER_ID && (
          <div className="absolute top-3 right-3 bg-accent-text text-white text-xs px-2 py-1 rounded-full">
            Official
          </div>
        )}
      </div>

      <div className="pt-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2 flex-1 min-w-0">
            <Avatar
              url={community.avatar_url}
              size="sm"
              userId={community.owner_id}
              editable={false}
            />
            <div className="min-w-0">
              <p className="text-sm text-content truncate">
                {community.owner?.username || community.name}
              </p>
              <p className="text-xs text-content/60 mt-0.5">Community</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="px-2 py-1 bg-accent-base/10 text-accent-text rounded-full text-xs font-medium">
              {community.type === 'private' ? t('common.private') : t('common.public')}
            </span>
            {distance !== undefined && (
              <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs flex items-center">
                <MapPin className="h-3 w-3 mr-1" />
                {distance < 1 ? `${Math.round(distance * 1000)}m` : `${distance.toFixed(1)}km`}
              </span>
            )}
            {community.owner_id === OFFICIAL_OWNER_ID && (
              <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-full">
                Official
              </span>
            )}
          </div>
        </div>

        <h3 className="text-lg font-semibold text-content mb-2 line-clamp-1">
          {community.name}
        </h3>

        <p className="text-content/80 text-sm line-clamp-2 leading-relaxed mb-4">
          {community.description || t('communities.joinToConnect')}
        </p>

        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
          {community.memberAvatars && community.memberAvatars.length > 0 ? (
            <AvatarGroup
              avatars={community.memberAvatars}
              max={4}
              size="xs"
              totalCount={community._count?.members || 0}
            />
          ) : (
            <div className="flex items-center text-xs text-content/50">
              <Users className="h-3.5 w-3.5 mr-1" />
              <span>{t('communities.beFirstToJoin')}</span>
            </div>
          )}
          <span className="text-xs text-content/50">
            {community._count?.members || 0} {t('communities.members')}
          </span>
        </div>
      </div>
    </div>
  );

  if (disabled) {
    return <div className="relative">{CardContent()}</div>;
  }

  return (
    <Link to={`/communities/${community.slug || community.id}`}>
      {CardContent()}
    </Link>
  );
}